import { pickBy, uniq } from 'ramda';
import type { AllEntities, Permission } from 'c-sdk';
import type {
    AdminPermissionGroups,
    PermissionByCategory,
    PermissionNamespace,
    PermissionsByNamespace,
    PermissionsDiff,
} from 'c-admin';
import { snake2PascalCase, stringEndsWithAny, stringStartsWithAny } from 'c-lib';

const AdminNamespace = 'admin';
export const AdminGeneralGroup = 'general';

const CreateSuffix = '.create';
const ReadSuffix = '.read';
const UpdateSuffix = '.update';
const DestroySuffix = '.destroy';

export const checkedPermissionIds = (formValues: Record<number, boolean>) =>
    Object.keys(pickBy(val => val === true, formValues));

export const permissionDiffs = (
    formValues: Record<number, boolean>,
    allPermissionIds: number[],
    rolePermissionIds: number[],
): PermissionsDiff =>
    allPermissionIds.reduce(
        (acc, permissionId) => {
            const permValue = formValues[permissionId] ?? false;
            if (rolePermissionIds.indexOf(permissionId) !== -1 && permValue === false) {
                // was there but now not selected
                acc.removed.push(permissionId);
            }
            if (rolePermissionIds.indexOf(permissionId) === -1 && permValue === true) {
                // was not there but now is selected
                acc.added.push(permissionId);
            }
            return acc;
        },
        { added: [], removed: [] } as PermissionsDiff,
    );

export const groupedPermissions = (
    permissions: Permission[],
    generalNamespaces: PermissionNamespace[] = [],
): PermissionByCategory => {
    const { crud, raw } = namespacedPermissions(permissions, generalNamespaces);
    return {
        // admin permissions get their own group, so keep them out of the flat general list
        general: uniq([
            ...onlyNotEntityPermissions(permissions),
            ...permissions.filter(p => stringStartsWithAny(p.name, generalNamespaces)),
        ]).filter(p => !isAdminPermission(p)),
        admin: adminPermissionGroups(permissions),
        namespaces: crud,
        rawPermissionsByNamespace: raw,
    };
};

export const isAdminPermission = (permission: Permission): boolean =>
    permission.name === AdminNamespace || permission.name.startsWith(`${AdminNamespace}.`);

/**
 * Groups every `admin.*` permission on the segment that follows `admin.`, so
 * `admin.campaign.cad` lands under `campaign` and `admin.page.frame_search` under `page`.
 * Permissions with nothing to group on - `admin`, `admin.can_impersonate_user` - go under
 * {@link AdminGeneralGroup}.
 */
export const adminPermissionGroups = (permissions: Permission[]): AdminPermissionGroups =>
    permissions.filter(isAdminPermission).reduce((acc, curr) => {
        const parts = curr.name.split('.');
        const group = parts.length > 2 ? parts[1] : AdminGeneralGroup;

        if (!Array.isArray(acc[group])) {
            acc[group] = [];
        }
        acc[group].push(curr);

        return acc;
    }, {} as AdminPermissionGroups);

/**
 * Label for a permission inside an admin group - the group prefix is already the heading, so
 * `admin.campaign.cad` in the `campaign` group reads as `cad`.
 */
export const adminPermissionLabel = (permissionName: string, group: string): string => {
    const prefix =
        group === AdminGeneralGroup ? `${AdminNamespace}.` : `${AdminNamespace}.${group}.`;
    const label = permissionName.startsWith(prefix)
        ? permissionName.slice(prefix.length)
        : permissionName;

    return (label === '' ? permissionName : label).replace(/[._]/g, ' ');
};

type NamespacedReturn = {
    crud: PermissionsByNamespace;
    raw: PermissionByCategory['rawPermissionsByNamespace'];
};

const namespacedPermissions = (
    permissions: Permission[],
    excludedNamespaces: PermissionNamespace[] = [],
): NamespacedReturn =>
    onlyEntityPermissions(permissions).reduce(
        (acc, curr) => {
            const parts = curr.name.split('.');
            const namespace = parts[0] as PermissionNamespace;

            // {namespace}.{model}.{create|read|update|delete}
            if (parts.length === 3 && excludedNamespaces.indexOf(namespace) === -1) {
                const entity = snake2PascalCase(parts[1]) as keyof AllEntities;

                if (acc.crud[namespace] == null) {
                    acc.crud[namespace] = {};
                }
                if (acc.crud[namespace][entity] == null) {
                    acc.crud[namespace][entity] = {};
                }

                if (curr.name.endsWith(CreateSuffix)) acc.crud[namespace][entity].create = curr;
                if (curr.name.endsWith(ReadSuffix)) acc.crud[namespace][entity].read = curr;
                if (curr.name.endsWith(UpdateSuffix)) acc.crud[namespace][entity].update = curr;
                if (curr.name.endsWith(DestroySuffix)) acc.crud[namespace][entity].delete = curr;

                if (!Array.isArray(acc.raw[namespace])) {
                    acc.raw[namespace] = [];
                }

                acc.raw[namespace].push(curr);
            }
            return acc;
        },
        { crud: {}, raw: {} } as NamespacedReturn,
    );

const onlyEntityPermissions = (permissions: Permission[]): Permission[] =>
    permissions.filter(
        p =>
            stringEndsWithAny(p.name, [CreateSuffix, ReadSuffix, UpdateSuffix, DestroySuffix]) &&
            p.name.split('.').length === 3,
    );

const onlyNotEntityPermissions = (permissions: Permission[]): Permission[] =>
    permissions.filter(
        p =>
            !stringEndsWithAny(p.name, [CreateSuffix, ReadSuffix, UpdateSuffix, DestroySuffix]) ||
            p.name.split('.').length !== 3,
    );
